<template>
  <div class="widget-grid-container">
    <!-- Toolbar para gestión de widgets -->
    <div class="widget-toolbar">
      <div class="toolbar-left">
        <h6 class="mb-0">
          <i class="ri-dashboard-3-line me-2"></i>
          Widgets Personalizables
        </h6>
      </div>
      <div class="toolbar-right">
        <el-button 
          size="small" 
          type="primary" 
          @click="showAddWidgetModal = true"
          icon="el-icon-plus"
        >
          <i class="ri-add-line me-1"></i>
          Añadir Widget
        </el-button>
        <el-button 
          size="small" 
          @click="resetLayout"
          icon="el-icon-refresh"
        >
          <i class="ri-refresh-line me-1"></i>
          Resetear Layout
        </el-button>
        <el-button 
          size="small" 
          @click="saveLayout"
          icon="el-icon-check"
        >
          <i class="ri-save-line me-1"></i>
          Guardar Layout
        </el-button>
      </div>
    </div>

    <!-- Grid de widgets draggable -->
    <div 
      ref="widgetGrid" 
      class="widget-grid"
      :class="{ 'grid-editing': isEditMode }"
    >
      <div
        v-for="widget in widgets"
        :key="widget.id"
        class="widget-container"
        :style="getWidgetStyle(widget)"
        :data-widget-id="widget.id"
      >
        <DraggableWidget
          :widget="widget"
          :widget-id="widget.id"
          @widget-action="handleWidgetAction"
          @widget-resize="handleWidgetResize"
        />
      </div>
      
      <!-- Placeholder para nuevos widgets -->
      <div 
        v-if="isEditMode && widgets.length === 0"
        class="empty-grid-placeholder"
      >
        <i class="ri-drag-drop-line"></i>
        <p>Arrastra widgets aquí o haz clic en "Añadir Widget"</p>
      </div>
    </div>

    <!-- Modal para añadir widgets -->
    <el-dialog
      v-model="showAddWidgetModal"
      title="Añadir Nuevo Widget"
      width="600px"
      class="add-widget-modal"
    >
      <div class="widget-gallery">
        <div class="gallery-section">
          <h6>Métricas</h6>
          <div class="widget-options">
            <div 
              v-for="option in widgetOptions.metrics"
              :key="option.type"
              class="widget-option"
              @click="selectWidgetOption(option)"
            >
              <i :class="option.icon"></i>
              <span>{{ option.name }}</span>
            </div>
          </div>
        </div>
        
        <div class="gallery-section">
          <h6>Gráficos</h6>
          <div class="widget-options">
            <div 
              v-for="option in widgetOptions.charts"
              :key="option.type"
              class="widget-option"
              @click="selectWidgetOption(option)"
            >
              <i :class="option.icon"></i>
              <span>{{ option.name }}</span>
            </div>
          </div>
        </div>
        
        <div class="gallery-section">
          <h6>Datos</h6>
          <div class="widget-options">
            <div 
              v-for="option in widgetOptions.data"
              :key="option.type"
              class="widget-option"
              @click="selectWidgetOption(option)"
            >
              <i :class="option.icon"></i>
              <span>{{ option.name }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddWidgetModal = false">Cancelar</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Modal de configuración de widget -->
    <el-dialog
      v-model="showConfigModal"
      title="Configurar Widget"
      width="500px"
    >
      <div v-if="selectedWidget">
        <el-form :model="widgetConfig" label-width="120px">
          <el-form-item label="Título">
            <el-input v-model="widgetConfig.title"></el-input>
          </el-form-item>
          <el-form-item label="Icono">
            <el-select v-model="widgetConfig.icon" placeholder="Seleccionar icono">
              <el-option
                v-for="icon in iconOptions"
                :key="icon.value"
                :label="icon.label"
                :value="icon.value"
              >
                <i :class="icon.value" class="me-2"></i>
                {{ icon.label }}
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Actualización">
            <el-select v-model="widgetConfig.refreshInterval">
              <el-option label="Manual" :value="0"></el-option>
              <el-option label="30 segundos" :value="30"></el-option>
              <el-option label="1 minuto" :value="60"></el-option>
              <el-option label="5 minutos" :value="300"></el-option>
              <el-option label="10 minutos" :value="600"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showConfigModal = false">Cancelar</el-button>
          <el-button type="primary" @click="saveWidgetConfig">Guardar</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import dragula from 'dragula'
import DraggableWidget from './DraggableWidget.vue'

export default {
  name: 'WidgetGrid',
  components: {
    DraggableWidget
  },
  emits: ['widget-data-request'],
  setup(_, { emit }) {
    const widgetGrid = ref(null)
    const dragulaInstance = ref(null)
    const isEditMode = ref(true)
    const showAddWidgetModal = ref(false)
    const showConfigModal = ref(false)
    const selectedWidget = ref(null)
    
    const widgets = ref([])
    const widgetConfig = reactive({
      title: '',
      icon: '',
      refreshInterval: 0
    })
    
    // Opciones de widgets disponibles
    const widgetOptions = {
      metrics: [
        { type: 'total-projects', name: 'Total Proyectos', icon: 'ri-funds-line' },
        { type: 'total-subprojects', name: 'Total Subproyectos', icon: 'ri-building-line' },
        { type: 'total-works', name: 'Total Obras', icon: 'ri-hammer-line' },
        { type: 'active-users', name: 'Usuarios Activos', icon: 'ri-user-line' },
        { type: 'pending-tasks', name: 'Tareas Pendientes', icon: 'ri-task-line' }
      ],
      charts: [
        { type: 'doughnut-chart', name: 'Gráfico Circular', icon: 'ri-pie-chart-line' },
        { type: 'bar-chart', name: 'Gráfico de Barras', icon: 'ri-bar-chart-grouped-line' },
        { type: 'line-chart', name: 'Gráfico de Líneas', icon: 'ri-line-chart-line' },
        { type: 'area-chart', name: 'Gráfico de Área', icon: 'ri-stack-line' }
      ],
      data: [
        { type: 'recent-projects', name: 'Proyectos Recientes', icon: 'ri-list-check' },
        { type: 'activity-feed', name: 'Feed de Actividad', icon: 'ri-notification-line' },
        { type: 'user-table', name: 'Tabla de Usuarios', icon: 'ri-user-settings-line' },
        { type: 'export-summary', name: 'Resumen Exportaciones', icon: 'ri-download-cloud-line' }
      ]
    }
    
    const iconOptions = [
      { value: 'ri-dashboard-line', label: 'Dashboard' },
      { value: 'ri-funds-line', label: 'Fondos' },
      { value: 'ri-building-line', label: 'Edificio' },
      { value: 'ri-hammer-line', label: 'Herramienta' },
      { value: 'ri-user-line', label: 'Usuario' },
      { value: 'ri-task-line', label: 'Tarea' },
      { value: 'ri-pie-chart-line', label: 'Gráfico Circular' },
      { value: 'ri-bar-chart-grouped-line', label: 'Gráfico Barras' }
    ]
    
    // Inicializar dragula
    const initializeDragula = () => {
      if (dragulaInstance.value) {
        dragulaInstance.value.destroy()
      }
      
      dragulaInstance.value = dragula([widgetGrid.value], {
        moves: (el, container, handle) => {
          return handle.classList.contains('widget-header') || 
                 handle.closest('.widget-header')
        },
        accepts: (el, target) => {
          return target === widgetGrid.value
        }
      })
      
      dragulaInstance.value.on('drop', () => {
        saveLayout()
      })
    }
    
    // Obtener estilo del widget
    const getWidgetStyle = (widget) => {
      return {
        gridColumn: `span ${widget.size?.cols || 1}`,
        gridRow: `span ${widget.size?.rows || 1}`,
        minHeight: `${(widget.size?.rows || 1) * 200}px`
      }
    }
    
    // Manejar acciones del widget
    const handleWidgetAction = async (action) => {
      switch (action.type) {
        case 'refresh':
          await refreshWidget(action.widgetId)
          break
        case 'configure':
          configureWidget(action.widgetId)
          break
        case 'export':
          exportWidget(action.widgetId)
          break
        case 'fullscreen':
          toggleFullscreen(action.widgetId)
          break
        case 'remove':
          removeWidget(action.widgetId)
          break
      }
    }
    
    // Manejar redimensionamiento de widget
    const handleWidgetResize = (resizeData) => {
      const widget = widgets.value.find(w => w.id === resizeData.widgetId)
      if (widget) {
        // Convertir píxeles a unidades de grid
        const cols = Math.ceil(resizeData.width / 300)
        const rows = Math.ceil(resizeData.height / 200)
        
        widget.size = { cols, rows }
        saveLayout()
      }
    }
    
    // Seleccionar opción de widget
    const selectWidgetOption = (option) => {
      const newWidget = createWidget(option)
      widgets.value.push(newWidget)
      showAddWidgetModal.value = false
      
      nextTick(() => {
        saveLayout()
      })
    }
    
    // Crear nuevo widget
    const createWidget = (option) => {
      const id = `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      return {
        id,
        type: option.type,
        title: option.name,
        icon: option.icon,
        size: { cols: 1, rows: 1 },
        position: { x: 0, y: 0 },
        props: getDefaultProps(option.type),
        loading: false,
        refreshInterval: 0,
        lastRefresh: new Date()
      }
    }
    
    // Obtener props por defecto según el tipo
    const getDefaultProps = (type) => {
      const propsMap = {
        'total-projects': {
          title: 'Total Proyectos',
          value: '0',
          percentage: { value: '+0%', color: 'text-success' },
          icon: { component: 'ri-funds-line', background: 'bg-gradient-primary' }
        },
        'doughnut-chart': {
          chartData: { labels: [], datasets: [] },
          chartOptions: { responsive: true, maintainAspectRatio: false }
        }
      }
      
      return propsMap[type] || {}
    }
    
    // Métodos de gestión de widgets
    const refreshWidget = async (widgetId) => {
      const widget = widgets.value.find(w => w.id === widgetId)
      if (widget) {
        widget.loading = true
        
        try {
          // Solicitar datos actualizados
          emit('widget-data-request', { type: widget.type, widgetId })
          
          setTimeout(() => {
            widget.loading = false
            widget.lastRefresh = new Date()
            ElMessage.success('Widget actualizado correctamente')
          }, 1000)
        } catch (error) {
          widget.loading = false
          ElMessage.error('Error al actualizar el widget')
        }
      }
    }
    
    const configureWidget = (widgetId) => {
      selectedWidget.value = widgets.value.find(w => w.id === widgetId)
      if (selectedWidget.value) {
        widgetConfig.title = selectedWidget.value.title
        widgetConfig.icon = selectedWidget.value.icon
        widgetConfig.refreshInterval = selectedWidget.value.refreshInterval
        showConfigModal.value = true
      }
    }
    
    const saveWidgetConfig = () => {
      if (selectedWidget.value) {
        selectedWidget.value.title = widgetConfig.title
        selectedWidget.value.icon = widgetConfig.icon
        selectedWidget.value.refreshInterval = widgetConfig.refreshInterval
        
        showConfigModal.value = false
        saveLayout()
        ElMessage.success('Configuración guardada')
      }
    }
    
    const exportWidget = () => {
      ElMessage.info('Funcionalidad de exportación en desarrollo')
    }
    
    const toggleFullscreen = () => {
      ElMessage.info('Funcionalidad de pantalla completa en desarrollo')
    }
    
    const removeWidget = (widgetId) => {
      ElMessageBox.confirm(
        '¿Estás seguro de eliminar este widget?',
        'Confirmar eliminación',
        {
          confirmButtonText: 'Eliminar',
          cancelButtonText: 'Cancelar',
          type: 'warning'
        }
      ).then(() => {
        widgets.value = widgets.value.filter(w => w.id !== widgetId)
        saveLayout()
        ElMessage.success('Widget eliminado')
      }).catch(() => {
        // Cancelado
      })
    }
    
    // Gestión del layout
    const saveLayout = () => {
      const layout = widgets.value.map(widget => ({
        id: widget.id,
        type: widget.type,
        title: widget.title,
        icon: widget.icon,
        size: widget.size,
        position: widget.position,
        refreshInterval: widget.refreshInterval
      }))
      
      localStorage.setItem('dashboard-widget-layout', JSON.stringify(layout))
      ElMessage.success('Layout guardado')
    }
    
    const loadLayout = () => {
      try {
        const saved = localStorage.getItem('dashboard-widget-layout')
        if (saved) {
          const layout = JSON.parse(saved)
          widgets.value = layout.map(item => ({
            ...item,
            props: getDefaultProps(item.type),
            loading: false,
            lastRefresh: new Date()
          }))
        }
      } catch (error) {
        // console.error('Error al cargar layout:', error)
        // Silenciosamente fallar al cargar layout guardado
      }
    }
    
    const resetLayout = () => {
      ElMessageBox.confirm(
        '¿Estás seguro de resetear el layout? Se perderán todas las personalizaciones.',
        'Resetear Layout',
        {
          confirmButtonText: 'Resetear',
          cancelButtonText: 'Cancelar',
          type: 'warning'
        }
      ).then(() => {
        widgets.value = []
        localStorage.removeItem('dashboard-widget-layout')
        ElMessage.success('Layout reseteado')
      }).catch(() => {
        // Cancelado
      })
    }
    
    onMounted(() => {
      loadLayout()
      nextTick(() => {
        initializeDragula()
      })
    })
    
    onUnmounted(() => {
      if (dragulaInstance.value) {
        dragulaInstance.value.destroy()
      }
    })
    
    return {
      widgetGrid,
      widgets,
      isEditMode,
      showAddWidgetModal,
      showConfigModal,
      selectedWidget,
      widgetConfig,
      widgetOptions,
      iconOptions,
      getWidgetStyle,
      handleWidgetAction,
      handleWidgetResize,
      selectWidgetOption,
      saveLayout,
      resetLayout,
      saveWidgetConfig
    }
  }
}
</script>

<style>
/* Estilos para el grid de widgets */
.widget-grid-container {
  width: 100%;
}

.widget-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  margin-bottom: 20px;
}

.toolbar-left h6 {
  margin: 0;
  color: inherit;
  font-weight: 600;
}

.toolbar-right {
  display: flex;
  gap: 8px;
}

.widget-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  min-height: 400px;
  position: relative;
}

.widget-grid.grid-editing {
  border: 2px dashed rgba(114, 9, 183, 0.3);
  border-radius: 12px;
  padding: 20px;
}

.widget-container {
  position: relative;
  transition: all 0.3s ease;
}

.empty-grid-placeholder {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 12px;
}

.empty-grid-placeholder i {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-grid-placeholder p {
  margin: 0;
  font-size: 16px;
}

/* Modal de widgets */
.add-widget-modal .el-dialog__body {
  padding: 20px;
}

.widget-gallery {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.gallery-section h6 {
  margin: 0 0 12px 0;
  color: #4361ee;
  font-weight: 600;
  border-bottom: 1px solid rgba(67, 97, 238, 0.2);
  padding-bottom: 8px;
}

.widget-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.widget-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.05);
  text-align: center;
}

.widget-option:hover {
  border-color: #4361ee;
  background: rgba(67, 97, 238, 0.1);
  transform: translateY(-2px);
}

.widget-option i {
  font-size: 24px;
  color: #4361ee;
  margin-bottom: 8px;
}

.widget-option span {
  font-size: 12px;
  font-weight: 500;
}

/* Dragula styles */
.gu-mirror {
  position: fixed !important;
  margin: 0 !important;
  z-index: 9999 !important;
  opacity: 0.8;
  transform: scale(1.02);
}

.gu-hide {
  display: none !important;
}

.gu-unselectable {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
}

.gu-transit {
  opacity: 0.2;
  transform: scale(0.98);
}

/* Dark mode styles */
body.dark-mode-active .widget-toolbar {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .toolbar-left h6 {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .widget-grid.grid-editing {
  border-color: rgba(79, 195, 247, 0.3);
}

body.dark-mode-active .empty-grid-placeholder {
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.3);
}

body.dark-mode-active .gallery-section h6 {
  color: #4fc3f7;
  border-bottom-color: rgba(79, 195, 247, 0.2);
}

body.dark-mode-active .widget-option {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

body.dark-mode-active .widget-option:hover {
  border-color: #4fc3f7;
  background: rgba(79, 195, 247, 0.1);
}

body.dark-mode-active .widget-option i {
  color: #4fc3f7;
}

body.dark-mode-active .widget-option span {
  color: rgba(255, 255, 255, 0.8);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .widget-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .toolbar-right {
    justify-content: center;
  }
  
  .widget-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .widget-options {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
}
</style>